/**
 * Platform L3 Taška★ offer@SP bag client (Wave 5 / NEW G8).
 * Absolute `/api/v1/customer-account/offer-favorites*` — NOT tenant ApiClient.
 * Parallel to salesPointContactsApi — never merge claim/transfer with SP ★.
 */

import { ApiError } from '../../shared/api/ApiClient.js';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';
import {
  pendingOfferFavoriteDedupeKey,
  readPendingOfferFavoriteRefs,
} from './pendingOfferFavorites.js';
import {
  OFFER_FAVORITE_ERROR_CODES,
  type ClaimCustomerOfferFavoriteRef,
  type ClaimCustomerOfferFavoritesBody,
  type ClaimCustomerOfferFavoritesResultDTO,
  type CustomerOfferFavoriteListItemDTO,
  type ListCustomerOfferFavoritesResultDTO,
  type RemoveCustomerOfferFavoriteResultDTO,
  type SetCustomerOfferFavoriteResultDTO,
} from './offerFavoritesTypes.js';

const OFFER_FAVORITES_PATH = '/api/v1/customer-account/offer-favorites';

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

export interface OfferFavoritesClientAuth {
  apiBaseUrl: string;
  signal?: AbortSignal;
}

function platformUrl(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}${path}`;
}

function authFetchInit(init: {
  method: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}): RequestInit {
  const headers: Record<string, string> = platformJsonHeaders(init.headers ?? {});
  return {
    method: init.method,
    headers,
    credentials: 'include',
    ...(init.body !== undefined ? { body: init.body } : {}),
    ...(init.signal !== undefined ? { signal: init.signal } : {}),
  };
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  let body: SuccessEnvelope<T> | ErrorEnvelope;
  try {
    body = (await response.json()) as SuccessEnvelope<T> | ErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }

  if (!response.ok || body.success !== true) {
    throw customerEnvelopeApiError(response.status, body);
  }

  return body.data;
}

function normalizeListItem(raw: unknown): CustomerOfferFavoriteListItemDTO | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const item = raw as Record<string, unknown>;
  const id = item.id;
  const title = typeof item.title === 'string' ? item.title : null;
  const name = typeof item.name === 'string' ? item.name : title;
  const salesPointContext = item.salesPointContext;
  const productContext = item.productContext;
  const tenantContext = item.tenantContext;
  const createdAt = item.createdAt;
  if (
    typeof id !== 'number' ||
    title === null ||
    name === null ||
    typeof createdAt !== 'string' ||
    salesPointContext === null ||
    typeof salesPointContext !== 'object' ||
    productContext === null ||
    typeof productContext !== 'object' ||
    tenantContext === null ||
    typeof tenantContext !== 'object'
  ) {
    return null;
  }
  const sp = salesPointContext as Record<string, unknown>;
  const pc = productContext as Record<string, unknown>;
  const tc = tenantContext as Record<string, unknown>;
  if (
    typeof sp.salesPointId !== 'number' ||
    typeof sp.code !== 'string' ||
    typeof sp.name !== 'string' ||
    typeof pc.productId !== 'number' ||
    typeof tc.tenantId !== 'number' ||
    typeof tc.tenantCode !== 'string' ||
    typeof tc.tenantName !== 'string'
  ) {
    return null;
  }
  let variantId: number | null = null;
  if (typeof pc.variantId === 'number') {
    variantId = pc.variantId;
  }
  return {
    id,
    title,
    name,
    imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : null,
    thumbnailUrl: typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl : null,
    price: typeof item.price === 'number' ? item.price : null,
    listable: item.listable !== false,
    salesPointContext: {
      salesPointId: sp.salesPointId,
      code: sp.code,
      name: sp.name,
    },
    productContext: {
      productId: pc.productId,
      variantId,
    },
    tenantContext: {
      tenantId: tc.tenantId,
      tenantCode: tc.tenantCode,
      tenantName: tc.tenantName,
    },
    createdAt,
  };
}

export function normalizeOfferFavoritesList(
  data: unknown,
): ListCustomerOfferFavoritesResultDTO {
  const raw =
    data !== null && typeof data === 'object'
      ? (data as { items?: unknown })
      : null;
  const itemsRaw = Array.isArray(raw?.items) ? raw.items : [];
  const items: CustomerOfferFavoriteListItemDTO[] = [];
  for (const row of itemsRaw) {
    const normalized = normalizeListItem(row);
    // Prefer hide non-listable (Wave 5.3) — fail-closed omit.
    if (normalized !== null && normalized.listable) {
      items.push(normalized);
    }
  }
  return { items };
}

/** GET `/api/v1/customer-account/offer-favorites` */
export async function fetchOfferFavorites(
  input: OfferFavoritesClientAuth,
): Promise<ListCustomerOfferFavoritesResultDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, OFFER_FAVORITES_PATH),
    authFetchInit({
      method: 'GET',
      signal: input.signal,
    }),
  );
  const data = await parseEnvelope<unknown>(response);
  return normalizeOfferFavoritesList(data);
}

/** POST `/api/v1/customer-account/offer-favorites/claim` */
export async function claimOfferFavorites(
  input: OfferFavoritesClientAuth & { body: ClaimCustomerOfferFavoritesBody },
): Promise<ClaimCustomerOfferFavoritesResultDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${OFFER_FAVORITES_PATH}/claim`),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  return parseEnvelope<ClaimCustomerOfferFavoritesResultDTO>(response);
}

/** POST `/api/v1/customer-account/offer-favorites/favorite` */
export async function setOfferFavorite(
  input: OfferFavoritesClientAuth & {
    body:
      | {
          salesPointId: number;
          productId: number;
          variantId?: number | null;
          favorite: boolean;
        }
      | {
          tenantCode: string;
          salesPointSlug: string;
          productId: number;
          variantId?: number | null;
          favorite: boolean;
        };
  },
): Promise<SetCustomerOfferFavoriteResultDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${OFFER_FAVORITES_PATH}/favorite`),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  return parseEnvelope<SetCustomerOfferFavoriteResultDTO>(response);
}

/** POST `/api/v1/customer-account/offer-favorites/remove` — Screen 6 trash */
export async function removeOfferFavorite(
  input: OfferFavoritesClientAuth & {
    body:
      | {
          salesPointId: number;
          productId: number;
          variantId?: number | null;
        }
      | {
          tenantCode: string;
          salesPointSlug: string;
          productId: number;
          variantId?: number | null;
        };
  },
): Promise<RemoveCustomerOfferFavoriteResultDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${OFFER_FAVORITES_PATH}/remove`),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  return parseEnvelope<RemoveCustomerOfferFavoriteResultDTO>(response);
}

export function isOfferFavoriteMembershipBlockedError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === OFFER_FAVORITE_ERROR_CODES.MEMBERSHIP_BLOCKED
  );
}

export function isOfferFavoriteNotListableError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === OFFER_FAVORITE_ERROR_CODES.OFFER_NOT_LISTABLE
  );
}

/** Marker: guest bag favorites already claimed for this customer. */
export const GUEST_OFFER_FAVORITES_TRANSFERRED_CUSTOMER_KEY =
  'customer:guestOfferFavoritesTransferredCustomerId';

interface GuestOfferFavoritesTransferMarker {
  customerId: number;
  keys: string[];
}

function readTransferMarker(): GuestOfferFavoritesTransferMarker | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(GUEST_OFFER_FAVORITES_TRANSFERRED_CUSTOMER_KEY);
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const row = parsed as { customerId?: unknown; keys?: unknown };
    if (typeof row.customerId !== 'number' || !(row.customerId > 0)) {
      return null;
    }
    const keys = Array.isArray(row.keys)
      ? row.keys.filter((k): k is string => typeof k === 'string' && k.length > 0)
      : [];
    return { customerId: row.customerId, keys };
  } catch {
    return null;
  }
}

function writeTransferMarker(marker: GuestOfferFavoritesTransferMarker): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      GUEST_OFFER_FAVORITES_TRANSFERRED_CUSTOMER_KEY,
      JSON.stringify(marker),
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Every establish (registration / OIDC land): copy **new** browser guest bag
 * favorites into the account via claim. Does **not** clear localStorage.
 * Parallel to `transferGuestFavoritesOnRegistrationSilent` — do not call into SP transfer.
 */
export async function transferGuestOfferFavoritesSilent(
  input: OfferFavoritesClientAuth & { customerId: number },
): Promise<void> {
  const marker = readTransferMarker();
  const alreadyKeys =
    marker !== null && marker.customerId === input.customerId
      ? new Set(marker.keys)
      : new Set<string>();
  const favorites = readPendingOfferFavoriteRefs();
  const pendingNew = favorites.filter(
    (fav) => !alreadyKeys.has(pendingOfferFavoriteDedupeKey(fav)),
  );
  if (pendingNew.length === 0) {
    if (marker === null || marker.customerId !== input.customerId) {
      writeTransferMarker({ customerId: input.customerId, keys: [...alreadyKeys] });
    }
    return;
  }

  const pendingNewKeys = pendingNew.map(pendingOfferFavoriteDedupeKey);
  const toClaim = pendingNew.slice(0, 20) as ClaimCustomerOfferFavoriteRef[];
  try {
    await claimOfferFavorites({
      ...input,
      body: { favorites: toClaim },
    });
    // Browser list intentionally retained. Burn claimed keys (incl. failed silent rows).
    writeTransferMarker({
      customerId: input.customerId,
      keys: [...new Set([...alreadyKeys, ...pendingNewKeys])].sort(),
    });
  } catch (err) {
    if (isOfferFavoriteMembershipBlockedError(err)) {
      return;
    }
    throw err;
  }
}
